import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@/lib/query';
import {
  getOrganizationUsers,
  updateOrganizationUser,
  deleteOrganizationUser,
} from '@/modules/users/api';
import {
  OrganizationUserDto,
  UpdateOrganizationUserDto,
  SuccessResponse,
} from '@cargovibe/shared-types';

const USERS_QUERY_KEY: string[] = ['organization-users'];

interface UpdateOrganizationUserVariables {
  userId: string;
  data: UpdateOrganizationUserDto;
}

export const useOrganizationUsers = (): UseQueryResult<OrganizationUserDto[]> => {
  return useQuery<OrganizationUserDto[]>({
    queryKey: USERS_QUERY_KEY,
    queryFn: getOrganizationUsers,
  });
};

export const useUpdateOrganizationUser = (): UseMutationResult<
  OrganizationUserDto,
  Error,
  UpdateOrganizationUserVariables
> => {
  const queryClient: QueryClient = useQueryClient();

  return useMutation<OrganizationUserDto, Error, UpdateOrganizationUserVariables>({
    mutationFn: ({ userId, data }: UpdateOrganizationUserVariables): Promise<OrganizationUserDto> =>
      updateOrganizationUser(userId, data),
    onSuccess: (): void => {
      void queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY });
    },
  });
};

export const useDeleteOrganizationUser = (): UseMutationResult<SuccessResponse, Error, string> => {
  const queryClient: QueryClient = useQueryClient();

  return useMutation<SuccessResponse, Error, string>({
    mutationFn: (userId: string): Promise<SuccessResponse> => deleteOrganizationUser(userId),
    onSuccess: (): void => {
      void queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY });
    },
  });
};
